import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { Button } from "@groot/ui/button";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { RecordPaymentDialog } from "@/components/RecordPaymentDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@groot/ui/dialog";
import { Input } from "@groot/ui/input";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { Pagination } from "@/app/shared/ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { useBills, useDeleteBill, useUpdateBill } from "@/hooks/api/useBills";
import { toast } from "@/lib/toast";
import { formatDate } from "@/lib/utils";
import { apiClient } from "@/services/api";
import { BillTrackingMode, type Bill, type BillingCycleType } from "@/types";
import { formatBillingCycle } from "./formatBillingCycle";
import dayjs from "dayjs";
import {
  ArrowDown,
  ArrowUp,
  Calendar,
  CheckCircle2,
  Copy,
  Plus,
  RotateCcw,
  Search,
  Trash2,
  Trash,
} from "lucide-react";
import { parseAsInteger, parseAsString, useQueryStates } from "nuqs";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

// Helper to get status text and color
function getStatus(bill: Bill) {
  const nextDate = dayjs(bill.nextBillingDate);
  const today = dayjs();
  const daysUntil = nextDate.diff(today, "day");

  if (daysUntil < 0) {
    return { text: `Overdue (${Math.abs(daysUntil)}d)`, color: "text-destructive" };
  }
  if (daysUntil === 0) {
    return { text: "Due today", color: "text-warning" };
  }
  if (daysUntil <= 7) {
    return { text: `Due in ${daysUntil}d`, color: "text-warning" };
  }

  return {
    text: formatDate(dayjs(bill.nextBillingDate).toDate()),
    color: "text-muted-foreground",
  };
}

const PAGE_SIZE = 20;

export function Bills() {
  const navigate = useNavigate();
  const [billToDelete, setBillToDelete] = useState<number | null>(null);
  const [billToMarkPaid, setBillToMarkPaid] = useState<Bill | null>(null);
  const [calendarLink, setCalendarLink] = useState<string | null>(null);

  // URL query state for pagination, sorting, and filtering
  const [queryParams, setQueryParams] = useQueryStates({
    search: parseAsString.withDefault(""),
    trackingMode: parseAsString,
    billingCycleType: parseAsString,
    sortBy: parseAsString.withDefault("nextBillingDate"),
    sortOrder: parseAsString.withDefault("asc"),
    page: parseAsInteger.withDefault(1),
  });

  const [showDeleted, setShowDeleted] = useState(false);

  const { data: billsData, isLoading } = useBills({
    isActive: showDeleted ? undefined : true,
    includeDeleted: showDeleted ? true : undefined,
    search: queryParams.search || undefined,
    trackingMode: queryParams.trackingMode
      ? (queryParams.trackingMode as BillTrackingMode)
      : undefined,
    billingCycleType: (queryParams.billingCycleType as BillingCycleType) || undefined,
    sortBy:
      (queryParams.sortBy as "name" | "amount" | "nextBillingDate" | "createdAt") ||
      "nextBillingDate",
    sortOrder: (queryParams.sortOrder as "asc" | "desc") || "asc",
    limit: PAGE_SIZE,
    offset: (queryParams.page - 1) * PAGE_SIZE,
  });
  const deleteBill = useDeleteBill();
  const updateBill = useUpdateBill();

  const bills = billsData?.bills || [];
  const total = billsData?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleOpenPaymentDialog = (bill: Bill) => {
    setBillToMarkPaid(bill);
  };

  const handleDelete = async () => {
    if (billToDelete) {
      await deleteBill.mutateAsync(billToDelete);
      setBillToDelete(null);
    }
  };

  const handleGetCalendarLink = async () => {
    try {
      const { url } = await apiClient.getBillCalendarLink();
      setCalendarLink(url);
    } catch {
      toast({
        title: "Error",
        description: "Failed to generate calendar link",
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = () => {
    if (calendarLink) {
      navigator.clipboard.writeText(calendarLink);
      toast({
        title: "Copied!",
        description: "Calendar link copied to clipboard",
      });
    }
  };

  if (isLoading && !bills.length) {
    return (
      <Page>
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  return (
    <>
      <Page>
        {/* Header */}
        <PageHeader
          title="Bills & Subscriptions"
          description={`${bills.length} ${bills.length === 1 ? "bill" : "bills"}${showDeleted ? " (deleted)" : ""}`}
          actions={
            <div className="flex gap-2">
              <Button
                variant={showDeleted ? "default" : "outline"}
                onClick={() => {
                  setShowDeleted(!showDeleted);
                  setQueryParams({ page: 1 });
                }}
                className="gap-2"
              >
                <Trash className="size-4" />
                {showDeleted ? "Active Bills" : "Trash"}
              </Button>
              <Button variant="outline" onClick={handleGetCalendarLink} className="gap-2">
                <Calendar className="size-4" />
                Sync to Calendar
              </Button>
              <Button onClick={() => navigate("/bills/new")} className="gap-2">
                <Plus className="size-4" />
                Add bill
              </Button>
            </div>
          }
          className="mb-8"
        />

        {/* Filters & Sort */}
        <div className="mb-6 flex flex-wrap gap-3 items-center">
          <div className="relative max-w-md flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/70" />
            <Input
              placeholder="Search bills..."
              value={queryParams.search}
              onChange={(e) => setQueryParams({ search: e.target.value || null, page: 1 })}
              className="pl-9 border-input"
            />
          </div>

          <Select
            value={queryParams.trackingMode || "all"}
            onValueChange={(value) =>
              setQueryParams({
                trackingMode: value === "all" ? null : value,
                page: 1,
              })
            }
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Tracking" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tracking</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="auto">Auto</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={queryParams.billingCycleType || "all"}
            onValueChange={(value) =>
              setQueryParams({
                billingCycleType: value === "all" ? null : value,
                page: 1,
              })
            }
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Frequency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cycles</SelectItem>
              <SelectItem value="day">Daily</SelectItem>
              <SelectItem value="week">Weekly</SelectItem>
              <SelectItem value="month">Monthly</SelectItem>
              <SelectItem value="year">Yearly</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1">
            <Select
              value={queryParams.sortBy}
              onValueChange={(value) =>
                setQueryParams({
                  sortBy: value as "name" | "amount" | "nextBillingDate" | "createdAt",
                })
              }
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nextBillingDate">Due date</SelectItem>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="amount">Amount</SelectItem>
                <SelectItem value="createdAt">Created</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="sm"
              className="size-9 p-0"
              onClick={() =>
                setQueryParams({
                  sortOrder: queryParams.sortOrder === "asc" ? "desc" : "asc",
                })
              }
              title={queryParams.sortOrder === "asc" ? "Ascending" : "Descending"}
            >
              {queryParams.sortOrder === "asc" ? (
                <ArrowUp className="size-4" />
              ) : (
                <ArrowDown className="size-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Bills Table */}
        {bills.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground mb-4">
              {queryParams.search ? "No bills found" : "No bills yet"}
            </p>
            {!queryParams.search && (
              <Button onClick={() => navigate("/bills/new")} variant="outline" className="gap-2">
                <Plus className="size-4" />
                Add your first bill
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="border border-border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted hover:bg-muted">
                    <TableHead className="font-medium text-foreground/80">Name</TableHead>
                    <TableHead className="font-medium text-foreground/80">Amount</TableHead>
                    <TableHead className="font-medium text-foreground/80">Frequency</TableHead>
                    <TableHead className="font-medium text-foreground/80">Next Due</TableHead>
                    <TableHead className="font-medium text-foreground/80">Account</TableHead>
                    <TableHead className="w-[140px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bills.map((bill) => {
                    const status = getStatus(bill);
                    return (
                      <TableRow
                        key={bill.id}
                        className="cursor-pointer hover:bg-muted border-border"
                        onClick={() => navigate(`/bills/${bill.id}`)}
                      >
                        <TableCell className="font-medium text-foreground">
                          <div className="flex items-center gap-2">
                            <span>{bill.name}</span>
                            {!bill.isActive && (
                              <span className="text-xs text-muted-foreground/70">(Inactive)</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-foreground">
                          <AmountDisplay amount={bill.amount} />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatBillingCycle(bill.billingCycleType, bill.billingCycleValue)}
                        </TableCell>
                        <TableCell className={`text-sm ${status.color}`}>{status.text}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {bill.account?.accountName || "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            {bill.isDeleted ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="size-8 p-0 text-muted-foreground/70 hover:text-success"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  await updateBill.mutateAsync({
                                    id: bill.id,
                                    data: { isDeleted: false },
                                  });
                                  toast({
                                    title: "Restored",
                                    description: `"${bill.name}" has been restored.`,
                                  });
                                }}
                                title="Restore bill"
                              >
                                <RotateCcw className="size-4" />
                              </Button>
                            ) : (
                              <>
                                {bill.trackingMode === "manual" && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="size-8 p-0 text-muted-foreground/70 hover:text-success"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenPaymentDialog(bill);
                                    }}
                                    title="Mark as paid"
                                  >
                                    <CheckCircle2 className="size-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="size-8 p-0 text-muted-foreground/70 hover:text-muted-foreground"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setBillToDelete(bill.id);
                                  }}
                                >
                                  <Trash2 className="size-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <Pagination
              currentPage={queryParams.page}
              totalPages={totalPages}
              totalItems={total}
              itemsPerPage={PAGE_SIZE}
              onPageChange={(page) => setQueryParams({ page })}
              isLoading={isLoading}
              itemName="bills"
            />
          </div>
        )}
      </Page>

      {/* Delete Confirmation Dialog */}
      <Dialog open={billToDelete !== null} onOpenChange={() => setBillToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Bill?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this bill? You can restore it from the Trash later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBillToDelete(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Paid Dialog */}
      <RecordPaymentDialog
        bill={billToMarkPaid}
        open={billToMarkPaid !== null}
        onOpenChange={(open) => !open && setBillToMarkPaid(null)}
      />

      {/* Calendar Link Dialog */}
      <Dialog open={calendarLink !== null} onOpenChange={(open) => !open && setCalendarLink(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sync to Calendar</DialogTitle>
            <DialogDescription>
              Use this link to subscribe to your bills calendar in Google Calendar, Apple Calendar,
              or Outlook.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 mt-2">
            <Input readOnly value={calendarLink || ""} className="font-mono" />
            <Button size="icon" variant="outline" onClick={copyToClipboard}>
              <Copy className="size-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setCalendarLink(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
